import { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import {
    Box,
    Container,
    TextField,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    Grid,
    Card,
    CardContent,
    Collapse,
    Switch,
    FormControlLabel,
    Button,
    FormHelperText,
    InputAdornment,
    Popper,
    Paper,
    List,
    ListItem,
    ListItemText,
    ClickAwayListener,
    Chip,
    Checkbox,
} from '@material-ui/core';
import {
    ArrowBack as ArrowBackIcon,
    ExpandLess,
    ExpandMore,
    KeyboardArrowDown,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createFulfilmentLocation, getFulfilmentLocations, updateFulfilmentLocation } from '../../../store/fulfilmentLocation/fulfilmentLocationActions';
import { getLocationCountries } from '../../../store/sellerLocation/sellerLocationActions';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
    },
    appBar: {
        backgroundColor: 'white',
        color: 'black',
        boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.1)',
    },
    toolbar: {
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    backButton: {
        marginRight: theme.spacing(2),
        backgroundColor: '#3f51b5',
        color: 'white',
        '&:hover': {
            backgroundColor: '#34495e',
        },
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#333',
    },
    content: {
        padding: theme.spacing(3),
    },
    sectionCard: {
        marginBottom: theme.spacing(3),
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
    },
    sectionHeader: {
        padding: theme.spacing(2, 3),
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f8f9fa',
        },
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: 500,
        color: '#333',
    },
    sectionContent: {
        padding: theme.spacing(3),
    },
    fieldLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#333',
        textAlign: "left",
        marginBottom: '4px',
        display: 'block',
    },
    centralWarehouseNameLabel: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#333',
        textAlign: 'left',
        marginBottom: theme.spacing(2),
        display: 'block',
    },
    textDanger: {
        color: 'red',
        marginLeft: '4px',
    },
    gridItem: {
        marginBottom: theme.spacing(2),
    },
    switchControl: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        border: '1px solid #e0e0e0',
        borderRadius: theme.spacing(0.5),
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(4),
        gap: theme.spacing(2),
    },
    backButtonStyle: {
        backgroundColor: 'white',
        color: '#333',
        border: '2px solid #e0e0e0',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 4),
        fontSize: '16px',
        fontWeight: 500,
        textTransform: 'none',
        minWidth: '150px',
        '&:hover': {
            backgroundColor: '#f8f9fa',
            borderColor: '#ccc',
        },
    },
    createButton: {
        backgroundColor: '#3f51b5',
        color: 'white',
        border: 'none',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 4),
        fontSize: '16px',
        fontWeight: 500,
        textTransform: 'none',
        minWidth: '200px',
        '&:hover': {
            backgroundColor: '#34495e',
        },
    },
    searchableSelect: {
        position: 'relative',
        width: '100%',
    },
    searchInput: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            cursor: 'pointer',
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#ccc',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
            },
        },
        '& .MuiInputBase-input': {
            fontSize: '14px',
            padding: '8.5px 14px',
            cursor: 'pointer',
        },
    },
    dropdownPaper: {
        maxHeight: 200,
        overflow: 'auto',
        marginTop: theme.spacing(0.5),
        zIndex: 1300,
    },
    listItem: {
        padding: theme.spacing(1, 2),
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
        '&.selected': {
            backgroundColor: '#e3f2fd',
        },
    },
    multiSelectChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
    },
    chip: {
        height: 24,
        fontSize: '12px',
    },
}));

// Custom Searchable Select Component
const SearchableSelect = ({
    field,
    form,
    options,
    placeholder,
    multiple = false,
    getOptionLabel = (option) => option.label || option,
    getOptionValue = (option) => option.value || option,
    isCentralWarehouse = false,
    isCountrySelect = false,
    ...props
}) => {
    const classes = useStyles();
    const { name } = field;
    const { touched, errors, setFieldValue } = form;
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [focused, setFocused] = useState(false);

    const hasError = touched[name] && errors[name];

    const filteredOptions = options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getDisplayValue = () => {
        const fieldValue = field.value;
        if (multiple) {
            return '';
        }
        if (!fieldValue) return '';

        if (isCentralWarehouse) {
            if (Array.isArray(fieldValue) && fieldValue.length > 0 && fieldValue[0].name) {
                return fieldValue[0].name;
            }
            return '';
        }
        const selectedOption = options.find(option =>
            getOptionValue(option) === fieldValue
        );
        return selectedOption ? getOptionLabel(selectedOption) : '';
    };

    const getSelectedChips = () => {
        if (!multiple || !field.value || field.value.length === 0) return [];
        if (isCentralWarehouse) {
            return options.filter(option => {
                const optionValue = getOptionValue(option);
                const optionId = Array.isArray(optionValue) && optionValue.length > 0 ? optionValue[0].id : null;
                return field.value.some(val => {
                    const valId = Array.isArray(val) && val.length > 0 ? val[0].id : val;
                    return valId === optionId;
                });
            });
        }
        return options.filter(option =>
            field.value.includes(getOptionValue(option))
        );
    };

    const handleInputClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

         // Check if click came from the dropdown icon
         const isIconClick =
         event.currentTarget.tagName === "BUTTON" ||
         event.currentTarget.closest("button");

       let anchorElement = event.currentTarget;

       // If clicking the icon, find the input field for proper positioning
       if (isIconClick) {
         const textField = event.currentTarget.closest(".MuiTextField-root");
         const inputElement = textField?.querySelector("input");
         anchorElement = inputElement || textField || event.currentTarget;

         // For icon clicks, toggle the dropdown
         if (open) {
           handleClose();
           return;
         }
       }
       

        if (!open) {
            setAnchorEl(anchorElement);
            setOpen(true);
            setFocused(true);
            if (!multiple) {
                setSearchTerm('');
            }
        }
    };

    const handleInputChange = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setSearchTerm(event.target.value);

        if (!open) {
            setAnchorEl(event.currentTarget);
            setOpen(true);
            setFocused(true);
        }
    };

    const handleInputFocus = (event) => {
        setFocused(true);
        if (!open) {
            handleInputClick(event);
        }
    };

    const handleInputBlur = (event) => {
        setFocused(false);
    };

    const handleClose = () => {
        setOpen(false);
        setFocused(false);
        setSearchTerm('');
    };

    const handleOptionClick = (event, option) => {
        event.preventDefault();
        event.stopPropagation();

        const optionValue = getOptionValue(option);

        if (isCountrySelect && option.code) {
            setFieldValue('countryCode', option.code);
        }

        if (multiple) {
            const currentValues = field.value || [];
            if (isCentralWarehouse) {
                const optionId = Array.isArray(optionValue) && optionValue.length > 0 ? optionValue[0].id : null;
                const isAlreadySelected = currentValues.some(val => {
                    const valId = Array.isArray(val) && val.length > 0 ? val[0].id : val;
                    return valId === optionId;
                });
                const newValues = isAlreadySelected
                    ? currentValues.filter(val => {
                        const valId = Array.isArray(val) && val.length > 0 ? val[0].id : val;
                        return valId !== optionId;
                    })
                    : [...currentValues, optionValue];
                setFieldValue(name, newValues);
            } else {
                const newValues = currentValues.includes(optionValue)
                    ? currentValues.filter(val => val !== optionValue)
                    : [...currentValues, optionValue];
                setFieldValue(name, newValues);
            }
            setSearchTerm('');
            return;
        }

        setFieldValue(name, optionValue);

        if (props.onSelectionChange && name === 'warehouseType') {
            props.onSelectionChange(optionValue);
        }

        handleClose();
    };

    const handleChipDelete = (event, optionToDelete) => {
        event.preventDefault();
        event.stopPropagation();

        const optionValue = getOptionValue(optionToDelete);
        if (isCentralWarehouse) {
            const optionId = Array.isArray(optionValue) && optionValue.length > 0 ? optionValue[0].id : null;
            const newValues = field.value.filter(val => {
                const valId = Array.isArray(val) && val.length > 0 ? val[0].id : val;
                return valId !== optionId;
            });
            setFieldValue(name, newValues);
        } else {
            const newValues = field.value.filter(val => val !== optionValue);
            setFieldValue(name, newValues);
        }
    };

    const isSelected = (option) => {
        const optionValue = getOptionValue(option);
        const fieldValue = field.value;

        if (multiple && isCentralWarehouse) {
            if (!Array.isArray(fieldValue)) return false;
            const optionId = Array.isArray(optionValue) && optionValue.length > 0 ? optionValue[0].id : null;
            return fieldValue.some(val => {
                const valId = Array.isArray(val) && val.length > 0 ? val[0].id : val;
                return valId === optionId;
            });
        }
        if (multiple) {
            return Array.isArray(fieldValue) && fieldValue.includes(optionValue);
        }
        if (isCentralWarehouse) {
            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                if (Array.isArray(optionValue) && optionValue.length > 0) {
                    return fieldValue[0].id === optionValue[0].id;
                }
            }
            return false;
        }
        return fieldValue === optionValue;
    };

    const getInputValue = () => {
        if (open || focused) {
            return searchTerm;
        }
        return multiple ? '' : getDisplayValue();
    };

    return (
        <Box className={classes.searchableSelect}>
            <TextField
                {...props}
                variant="outlined"
                size="small"
                placeholder={placeholder}
                value={getInputValue()}
                onChange={handleInputChange}
                onClick={handleInputClick}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                error={hasError}
                className={classes.searchInput}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleInputClick}
                                style={{ padding: 4 }}
                            >
                                <KeyboardArrowDown
                                    style={{
                                        transform: open ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.2s',
                                        fontSize: 20
                                    }}
                                />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                InputLabelProps={{
                    shrink: false,
                }}
            />

            {multiple && field.value && field.value.length > 0 && (
                <Box className={classes.multiSelectChips}>
                    {getSelectedChips().map((option, index) => (
                        <Chip
                            key={index}
                            label={getOptionLabel(option)}
                            onDelete={(event) => handleChipDelete(event, option)}
                            size="small"
                            className={classes.chip}
                        />
                    ))}
                </Box>
            )}

            <Popper
                open={open}
                anchorEl={anchorEl}
                style={{ width: anchorEl?.offsetWidth, zIndex: 1300 }}
                placement="bottom-start"
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper className={classes.dropdownPaper}>
                        <List dense>
                            {filteredOptions.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="No options found" />
                                </ListItem>
                            ) : (
                                filteredOptions.map((option, index) => (
                                    <ListItem
                                        key={index}
                                        button
                                        onClick={(event) => handleOptionClick(event, option)}
                                        className={`${classes.listItem} ${isSelected(option) ? 'selected' : ''}`}
                                    >
                                        {multiple && (
                                            <Checkbox
                                                checked={isSelected(option)}
                                                tabIndex={-1}
                                                disableRipple
                                                style={{ padding: '4px 8px' }}
                                            />
                                        )}
                                        <ListItemText primary={getOptionLabel(option)} />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {hasError && (
                <FormHelperText error>{errors[name]}</FormHelperText>
            )}
        </Box>
    );
};

function normalizeCentralWarehouseValue(cw, min_sla = '', max_sla = '') {
    if (cw == null || cw === '') return [];
    const list = Array.isArray(cw) ? cw : [cw];
    return list
        .map((entry) => {
            if (Array.isArray(entry) && entry[0]?.id) {
                return [{
                    ...entry[0],
                    min_sla: entry[0]?.min_sla ?? min_sla ?? '',
                    max_sla: entry[0]?.max_sla ?? max_sla ?? '',
                }];
            }
            if (entry && typeof entry === 'object' && entry.id) {
                return [{
                    id: entry.id,
                    name: entry.name || '',
                    min_sla: entry.min_sla ?? min_sla ?? '',
                    max_sla: entry.max_sla ?? max_sla ?? '',
                }];
            }
            return null;
        })
        .filter(Boolean);
}

function mergeCentralWarehousesWithSla(selectedWarehouses, existingWarehouses = [], defaultData = {}) {
    const normalizedSelected = normalizeCentralWarehouseValue(selectedWarehouses);
    return normalizedSelected.map((warehouseValue) => {
        const selected = warehouseValue[0];
        const existing = (existingWarehouses || []).find((item) => {
            const existingValue = Array.isArray(item) ? item[0] : item;
            return existingValue?.id === selected?.id;
        });
        const existingValue = Array.isArray(existing) ? existing[0] : existing;
        console.log('existingValue', existingValue);
        console.log('selected', selected);
        console.log('defaultData', defaultData);
        return [{
            ...selected,
            min_sla: existingValue?.min_sla ?? selected?.min_sla ?? defaultData.min_sla ?? '',
            max_sla: existingValue?.max_sla ?? selected?.max_sla ?? defaultData.max_sla ?? '',
        }];
    });
}

// Validation Schema
const validationSchema = Yup.object().shape({
    warehouseType: Yup.string().required('Warehouse Type is required'),
    country: Yup.string().required('Country is required'),
    wareHouseId: Yup.string().when('warehouseType', {
        is: "Central",
        then: Yup.string().required('Warehouse location ID is required'),
        otherwise: Yup.string().notRequired(),
    }),
    warehouseName: Yup.string().required('Warehouse Name is required'),
    state: Yup.string().required('State is required'),
    area: Yup.string().required('Area is required'),
    city: Yup.string().required('City is required'),
    address: Yup.string().required('Address is required'),
    centralWarehouse: Yup.mixed().when('warehouseType', {
        is: 'Consolidate',
        then: Yup.mixed()
            .test(
                'central-warehouse-sla',
                'Min SLA and Max SLA are required for each central warehouse',
                value => {
                    if (!Array.isArray(value) || value.length === 0) return false;
                    return value.every((item) => {
                        const warehouse = Array.isArray(item) ? item[0] : item;
                        if (!warehouse?.id) return false;
                        const min = Number(warehouse?.min_sla);
                        const max = Number(warehouse?.max_sla);
                        if (Number.isNaN(min) || Number.isNaN(max)) return false;
                        return max >= min;
                    });
                }
            )
            .required('Central Warehouse is required'),
        otherwise: Yup.mixed().notRequired(),
    })

});

export default function CreateFulfilmentLocation() {
    const classes = useStyles();
    const [sectionExpanded, setSectionExpanded] = useState(true);
    const [currentWarehouseType, setCurrentWarehouseType] = useState('');
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();
    const { fulfilmentLocations } = useSelector(state => state.fulfilmentLocation);
    const { locationsContries } = useSelector(state => state.sellerLocation);  
    const isEditMode = location.state?.editMode || false;
    const editData = location.state?.fulfilmentData || null;

    const getInitialValues = () => {
        if (isEditMode && editData) {
            return {
                warehouseType: editData.warehouse_type || '',
                country: editData.country || '',
                countryCode: editData.country_code || '',
                city: editData.city || '',
                address: editData.address || '',
                wareHouseId: editData.warehouse_id || '',
                warehouseName: editData.warehouse_name || '',
                centralWarehouse: normalizeCentralWarehouseValue(editData.central_warehouse, editData.min_sla, editData.max_sla),
                state: editData.state || '',
                area: editData.area || '',
                pinCode: editData.pin_code || '',
                lastMileFlag: editData.last_mile_flag || false,
                latitude: editData.latitude || '',
                longitude: editData.longitude || '',
                status: editData.status || true,
                isSyncToInventry: editData.is_sync_to_inventry || false,
                returnLocation: editData.return_location || false,
            };
        }
        return {
            warehouseType: '',
            country: '',
            countryCode: '',
            city: '',
            address: '',
            wareHouseId: '',
            warehouseName: '',
            centralWarehouse: [],
            state: '',
            area: '',
            pinCode: '',
            lastMileFlag: false,
            latitude: '',
            longitude: '',
            status: true,
            isSyncToInventry: true,
            returnLocation: false,
        };
    };

    const warehouseTypes = [
        { label: 'Consolidate', value: 'Consolidate' },
        { label: 'Central', value: 'Central' }
    ];

const countries = (locationsContries || [])
    .filter(
        (item) =>
            item.name === 'India' ||
            item.name === 'China' ||
            item.name === 'Turkey'
    )
    .map((item) => ({
        label: item.name,
        value: item.name,
        code: item.code
    }));

const countries2 = (locationsContries || [])
    .filter(
        (item) =>
            item.name === 'KSA' ||
            item.name === 'UAE' ||
            item.name === 'Bahrain' ||
            item.name === 'Kuwait'
    )
    .map((item) => ({
        label: item.name,
        value: item.name,
        code: item.code // Add code property
    }));

    const centralWarehouses = (fulfilmentLocations || [])
        .filter(item => item.warehouse_type === 'Central')
        .map(item => ({
            label: item.warehouse_name,
            value: [{ id: item.warehouse_id, name: item.warehouse_name }] || [],
        }));

const handleSubmit = async (values, { setSubmitting }) => {
    try {
        const uuid = uuidv4().replace(/-/g, '').toUpperCase();
        const random4 = uuid.substring(0, 4);
        
        const apiPayload = {
            warehouseType: values.warehouseType,
            country: values.country,
            countryCode: values.countryCode,
            city: values.city,
            address: values.address,
            warehouseId: values.warehouseType === "Consolidate" && !isEditMode && !editData ? `M${random4}` : values.wareHouseId,
            warehouseName: values.warehouseName,
            state: values.state,
            area: values.area,
            pinCode: values.pinCode,
            lastMileFlag: values.lastMileFlag,
            returnLocation: values.returnLocation,
            centralWarehouse: values.centralWarehouse,
            min_sla: parseInt(values.centralWarehouse?.[0]?.[0]?.min_sla) || 0,
            max_sla: parseInt(values.centralWarehouse?.[0]?.[0]?.max_sla) || 0,
            latitude: parseFloat(values.latitude),
            longitude: parseFloat(values.longitude),
            isSyncToInventry: isEditMode ? editData.is_sync_to_inventry : true, 
            status: true,
        };

        if (isEditMode && editData) {
            await dispatch(updateFulfilmentLocation(editData._id, apiPayload, () => {
                history.push("/location/fulfillmentLocation");
            }));
        } else {
            await dispatch(createFulfilmentLocation(apiPayload, () => {
                history.push("/location/fulfillmentLocation");
            }));
        }

    } catch (error) {
        console.log('Error submitting form:', error);
    } finally {
        setSubmitting(false);
    }
};


    const handleWarehouseTypeChange = (warehouseType, setFieldValue) => {
        setCurrentWarehouseType(warehouseType);
        if (warehouseType === 'Central') {
            setFieldValue('lastMileFlag', true);
            setFieldValue('returnLocation', true);
            setFieldValue('centralWarehouse', []);
        } else if (warehouseType === 'Consolidate') {
            setFieldValue('lastMileFlag', false);
            setFieldValue('returnLocation', false);
        }
    };

    const handleBackLocation = () => {
        history.push("/location/fulfillmentLocation");
    };

    useEffect(() => {
        dispatch(getFulfilmentLocations());
        dispatch(getLocationCountries());
        if (isEditMode && editData) {
            setCurrentWarehouseType(editData.warehouse_type || '');
        }
    }, [isEditMode, editData]);


    return (
        <div className={classes.root}>
            {/* App Bar */}
            <AppBar position="static" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <IconButton onClick={handleBackLocation} className={classes.backButton}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography className={classes.title}>
                        {isEditMode ? 'Edit Fulfillment Location' : 'Fulfillment Location'}
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Main Content */}
            <Container maxWidth="xl" className={classes.content}>
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                        <Form>
                            {/* Fulfillment Location Info Section */}
                            <Card className={classes.sectionCard}>
                                <div
                                    className={classes.sectionHeader}
                                    onClick={() => setSectionExpanded(!sectionExpanded)}
                                >
                                    <Typography className={classes.sectionTitle}>
                                        {isEditMode ? 'Edit Fulfillment Location' : 'Fulfillment Location'}
                                    </Typography>
                                    {sectionExpanded ? <ExpandLess /> : <ExpandMore />}
                                </div>
                                <Collapse in={sectionExpanded}>
                                    <CardContent className={classes.sectionContent}>
                                        <Grid container spacing={3}>
                                            {/* Warehouse Type */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Fulfillment Type
                                                <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field name="warehouseType">
                                                    {({ field, form }) => (
                                                        <SearchableSelect
                                                            field={field}
                                                            form={form}
                                                            options={warehouseTypes}
                                                            placeholder="Select Fulfillment Type"
                                                            onSelectionChange={(value) => handleWarehouseTypeChange(value, form.setFieldValue)}
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>

                                            {/* Country */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Country
                                                <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field name="country">
                                                    {({ field, form }) => (
                                                        <SearchableSelect
                                                            field={field}
                                                            form={form}
                                                            options={values.warehouseType === "Central" ? countries2 : countries}
                                                            placeholder="Select Country"
                                                            isCountrySelect={true} 
                                                        />
                                                    )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6} lg={3} md={2.4} className={classes.gridItem}>
                                                <Typography className={`${classes.fieldLabel} ${classes.requiredField}`}>
                                                    Fulfillment Location Name
                                                    <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="warehouseName"
                                                    value={values.warehouseName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.warehouseName && !!errors.warehouseName}
                                                    helperText={touched.warehouseName && errors.warehouseName}
                                                    placeholder="Warehouse Name"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>
                                            {values.warehouseType === "Central" && (
                                                <Grid item xs={12} sm={6} lg={3} md={2.4} className={classes.gridItem}>
                                                    <Typography className={`${classes.fieldLabel} ${classes.requiredField}`}>
                                                        Warehouse ID
                                                        <span className={classes.textDanger}>*</span>
                                                    </Typography>
                                                    <Field
                                                        as={TextField}
                                                        name="wareHouseId"
                                                        value={values.wareHouseId}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Warelocation ID"
                                                        error={touched.wareHouseId && !!errors.wareHouseId}
                                                        helperText={touched.wareHouseId && errors.wareHouseId}
                                                        fullWidth
                                                        InputLabelProps={{
                                                            shrink: false,
                                                        }}
                                                        disabled={isEditMode}
                                                    />
                                                </Grid>
                                            )}
                                            {(
                                                <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Central Warehouse
                                                        <span className={classes.textDanger}>{(values.warehouseType === 'Consolidate' || currentWarehouseType === 'Consolidate') && "*"}</span>
                                                    </Typography>
                                                    <Field name="centralWarehouse">
                                                        {({ field, form }) => (
                                                            <SearchableSelect
                                                                field={field}
                                                                form={form}
                                                                options={centralWarehouses.filter(item => item.value[0].id !== values.wareHouseId)}
                                                                placeholder="Select Central Warehouse"
                                                                multiple
                                                                isCentralWarehouse={true}
                                                                onSelectionChange={(selectedWarehouses) => {
                                                                    const mergedWarehouses = mergeCentralWarehousesWithSla(
                                                                        selectedWarehouses,
                                                                        values.centralWarehouse,
                                                                        editData.central_warehouse
                                                                    );
                                                                    form.setFieldValue('centralWarehouse', mergedWarehouses);
                                                                }}
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid>
                                            )}

                                            {/* State */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    State
                                                    <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="state"
                                                    value={values.state}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.state && !!errors.state}
                                                    helperText={touched.state && errors.state}
                                                    placeholder="State"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} lg={3} md={2.4} className={classes.gridItem}>
                                                <Typography className={`${classes.fieldLabel} ${classes.requiredField}`}>
                                                    City
                                                    <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="city"
                                                    value={values.city}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.city && !!errors.city}
                                                    helperText={touched.city && errors.city}
                                                    placeholder="City"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} lg={3} md={2.4} className={classes.gridItem}>
                                                <Typography className={`${classes.fieldLabel} ${classes.requiredField}`}>
                                                    Address
                                                    <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="address"
                                                    value={values.address}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.address && !!errors.address}
                                                    helperText={touched.address && errors.address}
                                                    placeholder="Address"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            {/* Area */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Area
                                                    <span className={classes.textDanger}>*</span>
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="area"
                                                    value={values.area}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.area && !!errors.area}
                                                    helperText={touched.area && errors.area}
                                                    placeholder="Area"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            {/* Pin code */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    PIN Code
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="pinCode"
                                                    value={values.pinCode}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    error={touched.pinCode && !!errors.pinCode}
                                                    helperText={touched.pinCode && errors.pinCode}
                                                    placeholder="PIN Code"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>
                                            {/* Last mile flag */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Last mile flag
                                                </Typography>
                                                <Box className={classes.switchControl}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={values.lastMileFlag}
                                                                onChange={(e) => setFieldValue('lastMileFlag', e.target.checked)}
                                                                color="primary"
                                                                disabled
                                                            />
                                                        }
                                                        label={values.lastMileFlag ? "true" : "false"}
                                                        labelPlacement="end"
                                                    />
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Latitude
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="latitude"
                                                    value={values.latitude}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    size="small"
                                                    type="number"
                                                    error={touched.latitude && !!errors.latitude}
                                                    helperText={touched.latitude && errors.latitude}
                                                    placeholder="Latitude"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Longitude
                                                </Typography>
                                                <Field
                                                    as={TextField}
                                                    name="longitude"
                                                    value={values.longitude}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant="outlined"
                                                    type="number"
                                                    size="small"
                                                    error={touched.longitude && !!errors.longitude}
                                                    helperText={touched.longitude && errors.longitude}
                                                    placeholder="Longitude"
                                                    fullWidth
                                                    InputLabelProps={{
                                                        shrink: false,
                                                    }}
                                                />
                                            </Grid>

                                            {/* Return Location */}
                                            <Grid item xs={12} sm={6} lg={3} className={classes.gridItem}>
                                                <Typography className={classes.fieldLabel}>
                                                    Return Location
                                                </Typography>
                                                <Box className={classes.switchControl}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={values.returnLocation}
                                                                onChange={(e) => setFieldValue('returnLocation', e.target.checked)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={values.returnLocation ? "true" : "false"}
                                                        disabled={values.warehouseType === 'Consolidate'}
                                                        labelPlacement="end"
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Collapse>
                            </Card>

                            {(
                                <Card className={classes.sectionCard}>
                                    <div className={classes.sectionHeader}>
                                        <Typography className={classes.sectionTitle}>
                                            Central Warehouse SLA
                                        </Typography>
                                    </div>
                                    <CardContent className={classes.sectionContent}>
                                        <Grid container spacing={3}>
                                            {(values.centralWarehouse || []).map((warehouseValue, index) => {
                                                const warehouse = Array.isArray(warehouseValue) ? warehouseValue[0] : warehouseValue;
                                                return (
                                                    <Grid item xs={12} key={warehouse?.id || index}>
                                                        <Typography className={classes.centralWarehouseNameLabel}>
                                                            {warehouse?.name || `Central Warehouse ${index + 1}`}
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} sm={6} lg={3}>
                                                                <TextField
                                                                    variant="outlined"
                                                                    size="small"
                                                                    type="number"
                                                                    label="Min SLA"
                                                                    value={warehouse?.min_sla || ''}
                                                                    onChange={(event) => {
                                                                        const updated = [...(values.centralWarehouse || [])];
                                                                        const current = Array.isArray(updated[index]) ? updated[index][0] : updated[index];
                                                                        updated[index] = [{ ...current, min_sla: event.target.value }];
                                                                        setFieldValue('centralWarehouse', updated);
                                                                    }}
                                                                    placeholder="Min SLA"
                                                                    fullWidth
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} lg={3}>
                                                                <TextField
                                                                    variant="outlined"
                                                                    size="small"
                                                                    type="number"
                                                                    label="Max SLA"
                                                                    value={warehouse?.max_sla || ''}
                                                                    onChange={(event) => {
                                                                        const updated = [...(values.centralWarehouse || [])];
                                                                        const current = Array.isArray(updated[index]) ? updated[index][0] : updated[index];
                                                                        updated[index] = [{ ...current, max_sla: event.target.value }];
                                                                        setFieldValue('centralWarehouse', updated);
                                                                    }}
                                                                    placeholder="Max SLA"
                                                                    fullWidth
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })}
                                            {typeof errors.centralWarehouse === 'string' && (
                                                <Grid item xs={12}>
                                                    <FormHelperText error>{errors.centralWarehouse}</FormHelperText>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Action Buttons */}
                            <Box className={classes.buttonContainer}>
                                <Button
                                    variant="outlined"
                                    className={classes.backButtonStyle}
                                    onClick={handleBackLocation}
                                    startIcon={<ArrowBackIcon />}
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.createButton}
                                    disabled={isSubmitting}
                                >
                                {isEditMode ? 'Update Fulfillment Location' : 'Create Fulfillment Location'}
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Container>
        </div>
    );
}